'use client';

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useSSOStore } from '@/lib/sso/store';
import type { SCIMGroup } from '@/lib/sso/types';
import { Users, Settings2 } from 'lucide-react';

interface GroupMappingTableProps {
  groups: SCIMGroup[];
}

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full access to all resources' },
  { value: 'member', label: 'Member', description: 'Standard user access' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

const PERMISSIONS = [
  { id: 'agent:create', label: 'Create Agents', category: 'Agents' },
  { id: 'agent:execute', label: 'Execute Agents', category: 'Agents' },
  { id: 'agent:delete', label: 'Delete Agents', category: 'Agents' },
  { id: 'connection:manage', label: 'Manage Connections', category: 'Connections' },
  { id: 'environment:deploy', label: 'Deploy to Environments', category: 'Environments' },
  { id: 'settings:manage', label: 'Manage Settings', category: 'Settings' },
];

export function GroupMappingTable({ groups }: GroupMappingTableProps) {
  const { updateGroupMapping } = useSSOStore();
  const [selectedGroup, setSelectedGroup] = useState<SCIMGroup | null>(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const handleRoleChange = async (groupId: string, role: string) => {
    try {
      await updateGroupMapping(groupId, role);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleOpenPermissions = (group: SCIMGroup) => {
    setSelectedGroup(group);
    setSelectedPermissions(group.mapped_permissions || []);
    setPermissionsDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedGroup) return;

    try {
      await updateGroupMapping(
        selectedGroup.id,
        selectedGroup.mapped_role || 'member',
        selectedPermissions
      );
      setPermissionsDialogOpen(false);
    } catch (error) {
      // Error handled by store
    }
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((p) => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="size-5" />
            Group Role Mapping
          </CardTitle>
          <CardDescription>
            Map identity provider groups to platform roles and permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="size-12 mx-auto mb-4 opacity-50" />
              <p>No groups synced yet</p>
              <p className="text-sm">Groups will appear here after the first SCIM sync</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Group Name</TableHead>
                  <TableHead>External ID</TableHead>
                  <TableHead className="text-center">Members</TableHead>
                  <TableHead>Mapped Role</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell className="font-medium">{group.display_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm font-mono">
                      {group.external_id.slice(0, 20)}...
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{group.member_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={group.mapped_role || ''}
                        onValueChange={(value) => handleRoleChange(group.id, value)}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {group.mapped_permissions && group.mapped_permissions.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {group.mapped_permissions.slice(0, 2).map((perm) => (
                            <Badge key={perm} variant="outline" className="text-xs">
                              {perm}
                            </Badge>
                          ))}
                          {group.mapped_permissions.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{group.mapped_permissions.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Default</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenPermissions(group)}
                      >
                        <Settings2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Additional Permissions</DialogTitle>
            <DialogDescription>
              Configure additional permissions for "{selectedGroup?.display_name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {Object.entries(
              PERMISSIONS.reduce((acc, perm) => {
                if (!acc[perm.category]) acc[perm.category] = [];
                acc[perm.category].push(perm);
                return acc;
              }, {} as Record<string, typeof PERMISSIONS>)
            ).map(([category, perms]) => (
              <div key={category}>
                <h4 className="text-sm font-medium mb-2">{category}</h4>
                <div className="space-y-2">
                  {perms.map((perm) => (
                    <div key={perm.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={perm.id}
                        checked={selectedPermissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <Label htmlFor={perm.id} className="text-sm font-normal cursor-pointer">
                        {perm.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePermissions}>Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
